import { FieldDescription, MapPersistedEntityColumns } from "@mapmycustomers/shared/types/entity";

// A structured entry, or a legacy flat field name
export type PersistedListEntry = FieldDescription | string;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const isPersistedListEntry = (entry: unknown): entry is PersistedListEntry =>
  typeof entry === "string" ||
  (isRecord(entry) &&
    typeof entry.fieldName === "string" &&
    typeof entry.displayOrder === "number");

// Own keys only, so a key like "constructor" never reads an inherited member
const readOwnArray = (source: unknown, key: string): undefined | unknown[] => {
  if (!isRecord(source) || !Object.prototype.hasOwnProperty.call(source, key)) {
    return undefined;
  }
  const value = source[key];
  return Array.isArray(value) ? value : undefined;
};

// Reads a stored pinned list for every level; another client writes it, so it is checked, never
// trusted. An array at `fields.<key>` wins, even an empty one; a flat `<key>` array is the
// fallback only with `flatFallback`. Returns `undefined` when none is stored, to tell it from [].
const readPersistedList = (
  mapConfiguration: Partial<MapPersistedEntityColumns> | undefined,
  key: string,
  { flatFallback }: { flatFallback: boolean }
): PersistedListEntry[] | undefined => {
  const list =
    readOwnArray(mapConfiguration?.fields, key) ??
    (flatFallback ? readOwnArray(mapConfiguration, key) : undefined);
  return list?.filter(isPersistedListEntry);
};

export default readPersistedList;
